import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, Zap } from "lucide-react";

let _cache = null;
let _inflight = null;

export function useNotificationsStatus() {
  const [status, setStatus] = useState(_cache);

  useEffect(() => {
    if (_cache) { setStatus(_cache); return; }
    if (!_inflight) {
      _inflight = api.get("/notifications/status")
        .then(r => { _cache = r.data; return r.data; })
        .catch(() => { _cache = { email: { configured: false }, sms: { configured: false } }; return _cache; })
        .finally(() => { _inflight = null; });
    }
    _inflight.then(d => setStatus(d));
  }, []);

  return status;
}

export function refreshNotificationsStatus() {
  _cache = null;
  return api.get("/notifications/status")
    .then(r => { _cache = r.data; return r.data; })
    .catch(() => null);
}

/**
 * SendLiveButton — fires a real send via the backend, with friendly states.
 * Pass `endpoint` (POST URL) and `channel` ("sms" | "email").
 * Renders nothing if the channel is not configured (caller should already
 * be using a fallback like copy/open-in-app, so we stay non-disruptive).
 */
export function SendLiveButton({ endpoint, channel, disabled, onSent, label, testid }) {
  const status = useNotificationsStatus();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const configured = status?.[channel]?.configured;
  if (!configured) return null;

  const onClick = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      const r = await api.post(endpoint, { channel });
      if (r?.data?.ok) {
        setSent(true);
        toast.success(`${channel === "sms" ? "SMS" : "Email"} sent live`);
        onSent?.(r.data);
        setTimeout(() => setSent(false), 4000);
      } else {
        toast.error(r?.data?.error || "Send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.error || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className="btn-primary text-xs"
      data-testid={testid}
      title={`Auto-send via ${channel === "sms" ? "Twilio SMS" : "ServiceSell PRO email"}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : sent ? <Zap className="w-3.5 h-3.5" />
        : <Send className="w-3.5 h-3.5" />}
      {sent ? "Sent" : (label || `Send ${channel === "sms" ? "SMS" : "email"} now`)}
    </button>
  );
}
